package ui.widget

import androidx.compose.animation.core.*
import androidx.compose.foundation.*
import androidx.compose.foundation.BasicTooltipState
import androidx.compose.material3.*
import androidx.compose.runtime.*
import kotlinx.coroutines.*
import kotlin.time.*
import kotlin.time.Duration.Companion.seconds


@OptIn(ExperimentalMaterial3Api::class)
@Stable
class CustomTooltipState private constructor(
    initialIsVisible: Boolean,
    val timeout: Duration,
    override val isPersistent: Boolean,
    private val mutatorMutex: MutatorMutex
) : TooltipState {

    @OptIn(ExperimentalFoundationApi::class)
    companion object {
        @Composable
        @ExperimentalMaterial3Api
        fun rememberState(
            initialIsVisible: Boolean = false,
            isPersistent: Boolean = false,
            timeout: Duration = 5.seconds,
            mutatorMutex: MutatorMutex = BasicTooltipDefaults.GlobalMutatorMutex
        ): TooltipState =
            remember(
                isPersistent,
                mutatorMutex
            ) {
                CustomTooltipState(
                    initialIsVisible = initialIsVisible,
                    timeout = timeout,
                    isPersistent = isPersistent,
                    mutatorMutex = mutatorMutex
                )
            }
    }

    override val transition: MutableTransitionState<Boolean> =
        MutableTransitionState(initialIsVisible)

    override val isVisible: Boolean
        get() = transition.currentState || transition.targetState

    /**
     * continuation used to clean up
     */
    private var job: (CancellableContinuation<Unit>)? = null

    /**
     * Show the tooltip associated with the current [BasicTooltipState].
     * When this method is called, all of the other tooltips associated
     * with [mutatorMutex] will be dismissed.
     *
     * @param mutatePriority [MutatePriority] to be used with [mutatorMutex].
     */
    override suspend fun show(
        mutatePriority: MutatePriority
    ) {
        val cancellableShow: suspend () -> Unit = {
            suspendCancellableCoroutine { continuation ->
                transition.targetState = true
                job = continuation
            }
        }

        // Show associated tooltip for [TooltipDuration] amount of time
        // or until tooltip is explicitly dismissed depending on [isPersistent].
        mutatorMutex.mutate(mutatePriority) {
            try {
                if (isPersistent) {
                    cancellableShow()
                } else {
                    withTimeout(timeout) {
                        cancellableShow()
                    }
                }
            } finally {
                // timeout or cancellation has occurred
                // and we close out the current tooltip.
                dismiss()
            }
        }
    }

    /**
     * Dismiss the tooltip associated with
     * this [TooltipState] if it's currently being shown.
     */
    override fun dismiss() {
        transition.targetState = false
    }

    /**
     * Cleans up [mutatorMutex] when the tooltip associated
     * with this state leaves Composition.
     */
    override fun onDispose() {
        job?.cancel()
    }
}
